import { html, nothing } from "lit";
import type { McpSetupRequest } from "../controllers/mcp-setup";

export type McpSetupPromptProps = {
  queue: McpSetupRequest[];
  inputs: Record<string, Record<string, string>>;
  busy: string | null;
  onInput: (requestId: string, envName: string, value: string) => void;
  onSubmit: (request: McpSetupRequest) => void;
  onCancel: (request: McpSetupRequest) => void;
};

function logoUrl(domain: string): string {
  return `https://logo.clearbit.com/${domain}`;
}

export function renderMcpSetupPrompt(props: McpSetupPromptProps) {
  if (props.queue.length === 0) return nothing;

  const request = props.queue[0];
  const envValues = props.inputs[request.id] ?? {};
  const allFilled = request.requiredEnv.every(
    (f) => (envValues[f.name] ?? "").trim().length > 0,
  );
  const busy = props.busy === request.id;

  return html`
    <div class="mcp-setup-prompt" style="
      margin: 12px 0;
      border: 2px solid var(--accent, #4ecdc4);
      border-radius: var(--radius-md, 8px);
      padding: 16px;
      background: var(--bg-raised, var(--bg));
    ">
      <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
        ${request.domain
          ? html`<img src=${logoUrl(request.domain)} alt="" width="28" height="28"
              style="border-radius: 6px; flex-shrink: 0;"
              @error=${(e: Event) => ((e.target as HTMLImageElement).style.display = "none")} />`
          : nothing}
        <div style="flex: 1;">
          <div style="font-weight: 600; font-size: 15px;">
            Connect ${request.service}
          </div>
          <div class="muted" style="font-size: 12px;">${request.description}</div>
        </div>
        ${props.queue.length > 1
          ? html`<span class="chip chip-muted" style="font-size: 11px;">
              ${props.queue.length - 1} more pending
            </span>`
          : nothing}
      </div>

      ${request.requiredEnv.map(
        (field) => html`
          <div style="margin-bottom: 8px;">
            <label style="font-size: 12px; color: var(--text-muted); display: block; margin-bottom: 4px;">
              ${field.label}
            </label>
            <input type="password"
              .value=${envValues[field.name] ?? ""}
              @input=${(e: Event) =>
                props.onInput(request.id, field.name, (e.target as HTMLInputElement).value)}
              placeholder=${field.name}
              style="width: 100%; font-size: 13px; padding: 8px 12px; border: 1px solid var(--border); border-radius: var(--radius-sm, 4px); background: var(--bg); color: var(--text);"
            />
          </div>
        `,
      )}

      <div style="display: flex; gap: 8px; margin-top: 12px;">
        <button class="btn primary" style="flex: 1;"
          ?disabled=${busy || !allFilled}
          @click=${() => props.onSubmit(request)}>
          ${busy ? "Connecting..." : "Connect"}
        </button>
        <button class="btn" style="flex-shrink: 0;"
          ?disabled=${busy}
          @click=${() => props.onCancel(request)}>
          Cancel
        </button>
      </div>
    </div>
  `;
}
